import { Volume2, Lightbulb, Video, CalendarRange } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading, useDocTitle } from "../components/Reveal";
import { MediaImg } from "../components/MediaImg";
import { CTABanner } from "../components/CTABanner";
import { GoldLink } from "../components/Buttons";
import { IMAGES } from "../data/site";

const SERVICES = [
  {
    icon: Volume2,
    title: "Live Sound",
    copy: "Clean, even coverage from front row to back wall. Systems sized to the room, engineers who mix for the audience — not the desk.",
    image: IMAGES.soundCheck,
  },
  {
    icon: Lightbulb,
    title: "Lighting Design",
    copy: "Looks programmed per artist, per moment. Atmosphere that makes phones come out and sponsors smile.",
    image: IMAGES.stage,
  },
  {
    icon: Video,
    title: "Livestream & Multi-Cam",
    copy: "Broadcast-grade streams with redundancy built in — bonded internet, backup encoders, and graphics that carry your sponsors.",
    image: IMAGES.crowd,
  },
  {
    icon: CalendarRange,
    title: "Full Event Production",
    copy: "Stage management, show flow, vendor wrangling, and run-of-show discipline. You host; we run the room.",
    image: IMAGES.guitar,
  },
];

const SPECS = [
  ["PA Coverage", "50 – 5,000 capacity rooms"],
  ["Audio Consoles", "Digital, 32–64ch, multitrack recording"],
  ["Lighting", "Moving heads, LED washes, haze & atmosphere"],
  ["Video", "Up to 12-camera 4K switching & ISO records"],
  ["Streaming", "Redundant encoders · bonded cellular backup"],
  ["Power & Rigging", "Distribution, cable ramps, ground support"],
  ["Crew", "Engineers, LDs, stage managers, camera ops"],
];

export default function LiveProduction() {
  useDocTitle("Live Production");
  return (
    <div data-testid="live-production-page">
      <PageHero
        dark
        eyebrow="Production & Events"
        lines={[<>When it has to <span className="text-gold">work live, it's us.</span></>]}
        sub="Corporate production, livestreams, event AV, sound and lighting — the technical arm of a full creative company, not a separate vendor."
      >
        <div className="mt-9 flex flex-wrap gap-4">
          <GoldLink to="/start-a-project" testId="live-hero-cta">Book Your Date</GoldLink>
        </div>
      </PageHero>

      <section className="bg-night pb-20 text-sand sm:pb-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <div className="grid gap-6 sm:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.07}>
                <div
                  data-testid={`live-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group relative overflow-hidden border border-sand/10"
                >
                  <MediaImg
                    src={s.image}
                    alt={s.title}
                    className="h-80 w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-105 group-hover:opacity-40"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-night via-night/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-7">
                    <s.icon size={24} className="text-gold" />
                    <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-2.5 max-w-md text-sm leading-relaxed text-sand/70">{s.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Capability Sheet"
              title={<>The rig, <span className="text-gold-dark">in plain English.</span></>}
              sub="You don't need to speak dB and DMX. Here's what the numbers mean for your event."
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-sm leading-relaxed text-smoke">
                Every system is pre-built and line-checked in our shop before it touches your venue. If a
                cable can fail, we carry two. If the internet can drop, the stream has a backup. And because
                production is one arm of a full creative company, the team running your show can also design
                the stage graphics, cut the recap film, and build the event page.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="border border-ink/10 bg-white">
                {SPECS.map(([k, v], i) => (
                  <div
                    key={k}
                    className={`grid grid-cols-2 gap-4 px-6 py-4.5 sm:px-8 ${i !== SPECS.length - 1 ? "border-b border-ink/10" : ""}`}
                  >
                    <p className="py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-dark">{k}</p>
                    <p className="py-1 text-sm text-ink">{v}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-ink/40">
              Rooms & grounds we regularly run
            </p>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 font-display text-sm font-bold uppercase tracking-widest text-ink/45">
              {["Theaters", "Festival Grounds", "Houses of Worship", "Convention Halls", "Breweries", "Schools", "Rooftops", "Fairgrounds"].map((v) => (
                <span key={v}>{v}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner
        title="Dates fill fast. Especially summer."
        sub="Tell us the date, the venue, and the crowd size — we'll send a production plan, not a parts list."
      />
    </div>
  );
}
